import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import type { CellValue } from 'exceljs'
import type { ExpenseImportRow } from '@super-mega/server/contracts/expense'

dayjs.extend(utc)

export const EXPENSE_IMPORT_HEADERS = ['付款主体', '付款对象', '业务', '应付日期', '金额', '备注'] as const
function text(value: CellValue): string {
  if (value == null) {
    return ''
  }
  if (value instanceof Date) {
    return dayjs.utc(value).format('YYYY-MM-DD')
  }
  if (typeof value === 'object') {
    if ('formula' in value || 'sharedFormula' in value) {
      throw new Error('请将公式转换为值后导入')
    }
    if ('richText' in value) {
      return value.richText
        .map((part) => part.text)
        .join('')
        .trim()
    }
    if ('text' in value) {
      return value.text.trim()
    }
    throw new Error('单元格包含不支持的数据')
  }
  return String(value).trim()
}
export async function readExpenseFile(buffer: ArrayBuffer): Promise<ExpenseImportRow[]> {
  const { Workbook } = await import('exceljs')
  const book = new Workbook()
  await book.xlsx.load(buffer)
  const sheet = book.worksheets.at(0)
  if (!sheet || sheet.rowCount > 501) {
    throw new Error('每次最多导入 500 条，请使用模板填写')
  }
  if (EXPENSE_IMPORT_HEADERS.some((name, i) => text(sheet.getRow(1).getCell(i + 1).value) !== name)) {
    throw new Error('表头不匹配，请使用六列导入模板')
  }
  const rows: ExpenseImportRow[] = []
  for (let i = 2; i <= sheet.rowCount; i++) {
    try {
      const row = sheet.getRow(i)
      const values = EXPENSE_IMPORT_HEADERS.map((_, col) => text(row.getCell(col + 1).value))
      // 保留中间空行以确保后端错误行号与原 Excel 一致；仅忽略尾部空行。
      rows.push({
        organization: values[0],
        payee: values[1],
        business: values[2],
        dueAt: values[3],
        amount: values[4],
        note: values[5]
      })
    } catch (error) {
      throw new Error(`第 ${i} 行：${error instanceof Error ? error.message : '无法读取'}`)
    }
  }
  while (rows.length && Object.values(rows[rows.length - 1]).every((value) => !value)) {
    rows.pop()
  }
  if (!rows.length) {
    throw new Error('文件中没有支出记录')
  }
  return rows
}
export async function downloadExpenseTemplate() {
  const { Workbook } = await import('exceljs')
  const book = new Workbook(),
    sheet = book.addWorksheet('支出账单')
  sheet.addRow([...EXPENSE_IMPORT_HEADERS])
  sheet.columns.forEach((column, index) => {
    column.width = index === 5 ? 40 : 24
    column.numFmt = '@'
  })
  sheet.getRow(1).font = { bold: true }
  sheet.getCell('D1').note = '日期格式：YYYY-MM-DD，例如 2026-10-07。'
  sheet.getCell('E1').note = '非负金额，最多两位小数。'
  sheet.getCell('A1').note = '填写系统中已有的付款主体完整名称。付款对象与业务也按完整名称匹配。'
  const bytes = await book.xlsx.writeBuffer()
  const url = URL.createObjectURL(
    new Blob([new Uint8Array(bytes)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  )
  const link = document.createElement('a')
  link.href = url
  link.download = '支出账单导入模板.xlsx'
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
